<script>
import './PromoCodesGenerator.styl'
import {promoCodesApi} from '@/api/admin/promoCodes.js';
import {mapGetters} from 'vuex';
import PlaneCheckbox from '@/components/common/Checkbox/PlaneCheckbox.vue';
import SvgIcon from '@/components/common/SvgIcon/SvgIcon';
import InfoPopup from '@/components/common/modals/InfoPopup';
import QuestionPopup from '@/components/common/modals/QuestionPopup';
import Spinner from '@/components/common/Spinner/Spinner.vue';
import moment from 'moment';

export default {
  name: 'promo-codes-generator',
  data: () => ({
    discountType: 'trial',
    percent: null,
    amount: null,
    promoCode: '',
    attributedTo: '',
    promoCodes: [],
    tiers: [],
    trialDuration: null,
    selectedTrialPeriod: '',
    selectedTier: '',
    selectedPlan: '',
    isOneTime: false,
    pendingRemovePromoCodeId: null,
    loading: false,
    expires: null,
    dateFormat: 'YYYY-MM-DD',
    ops: {
      rail: {
        size: '3px',
      },
      bar: {
        size: '3px',
        background: 'rgba(0,0,0,0.1)',
      },
    },
  }),
  computed: {
    ...mapGetters([
      'getPeriods',
      'getPeriodById',
      'getEnrichedSubscriptionsPlans',
      'getActiveEnrichedSubscriptionsPlans',
    ]),
  },
  async mounted () {
    try {
      await this.$store.dispatch('fetchEnrichedSubscriptionsPlans')
      await this.$store.dispatch('fetchPeriods')
    } catch (e) {
      console.error(e)
    }
  },
  async created () {
    await this.reloadPromoCodes();
    this.tiers = await this.getTiers();
  },
  methods: {
    copy (text) {
      navigator.clipboard.writeText(text);
    },
    getDisplayDate (dateString, dateAndTime = true) {
      if (!dateString) return ''

      const date = new Date(dateString);

      if (dateAndTime) {
        return `${new Date(dateString).toLocaleString('en-US')}`;
      }

      return date.toLocaleDateString('en-US');
    },
    getDisplayTrial (promo) {
      if (promo.trialPeriodId) {
        return `${promo.trialDuration} ${this.getPeriodById(promo.trialPeriodId)?.name}`;
      }
    },
    async reloadPromoCodes () {
      await this.executeIntegration(async () => {
        try {
          const promoData = await promoCodesApi.getActivePromoCodes();
          this.promoCodes = promoData.data?.promos;
        } catch (e) {
          console.error(e)
        }
      })
    },
    async generatePromoCode () {
      await this.executeIntegration(async () => {
        try {
          const response = await promoCodesApi.generatePromoCode();

          if (response.data !== null) {
            this.promoCode = response.data;
          } else {
            console.log(response);
            this.showInfoDialog('Something went wrong');
          }
        } catch (e) {
          console.log(e)
          this.showInfoDialog('Something went wrong');
        }
      });
    },
    async savePromoCode () {
      if (!this.validateFields()) {
        return
      }

      const data = {
        promo: {
          name: this.attributedTo,
          code: this.promoCode,
          isOneTime: this.isOneTime,
          amount: this.amount,
          percent: this.percent,
          trialPeriodId: this.selectedTrialPeriod,
          trialDuration: this.trialDuration,
          subscriptionPlanTier: this.selectedTier,
          subscriptionPlanId: this.selectedPlan,
          expires: this.expires
            ? this.expires.clone().endOf('day').format('YYYY-MM-DDTHH:mm:ss')
            : null,
        },
      }

      await this.executeIntegration(async () => {
        try {
          const response = await promoCodesApi.savePromoCode(data);

          if (response.data !== null) {
            await this.reloadPromoCodes();
            this.clearSpecs();
          } else {
            console.log(response);
            this.showInfoDialog('Something went wrong');
          }
        } catch (e) {
          console.log(e)
          this.showInfoDialog('Something went wrong');
        }
      })
    },
    showRemovePromoCodeModal (id) {
      this.pendingRemovePromoCodeId = id;
      this.$modal.show('questionPopupPromoCodeRemove');
    },
    async removePromoCode () {
      if (!this.pendingRemovePromoCodeId) {
        return;
      }

      const id = this.pendingRemovePromoCodeId;
      this.pendingRemovePromoCodeId = null;

      await this.executeIntegration(async () => {
        try {
          await promoCodesApi.removePromoCode(id);

          await this.reloadPromoCodes();
        } catch (e) {
          console.log(e)
          this.showInfoDialog('Something went wrong');
        }
      })
    },
    validateFields () {
      if (!this.promoCode) {
        this.showInfoDialog('Promo code not entered');
        return false
      }
      if (this.discountType === 'trial' && !(this.trialDuration && this.selectedTrialPeriod)) {
        this.showInfoDialog('Not entered required fields');
        return false;
      }
      if (this.discountType === 'percentage' && !this.percent) {
        this.showInfoDialog('Not entered required fields');
        return false;
      }
      if (this.discountType === 'amount' && !this.amount) {
        this.showInfoDialog('Not entered required fields');
        return false;
      }
      return true;
    },
    setDiscountType (type) {
      this.discountType = type

      if (type === 'trial') {
        this.percent = null
        this.amount = null
      }
      if (type === 'percentage') {
        this.amount = null
        this.trialDuration = null
        this.selectedTrialPeriod = ''
      }
      if (type === 'amount') {
        this.percent = null
        this.trialDuration = null
        this.selectedTrialPeriod = ''
      }
    },
    clearSpecs () {
      this.discountType = 'trial'
      this.percent = null
      this.amount = null
      this.trialDuration = null
      this.selectedTrialPeriod = ''
      this.selectedTier = ''
      this.selectedPlan = ''
      this.expires = null
    },
    showInfoDialog (title) {
      this.$modal.show('InfoPopup', { title: title });
    },
    async executeIntegration (callback) {
      this.loading = true;

      try {
        await callback()
      } finally {
        this.loading = false;
      }
    },
    getTiers () {
      const plans = this.getActiveEnrichedSubscriptionsPlans;

      return plans.reduce((acc, current) => {
        if (!acc.find(item => item.id === current.tier)) {
          acc.push({id: current.tier, name: current.code})
        }

        return acc;
      }, []);
    },
    getTierName (tier) {
      return tier
        ? this.tiers.find(t => t.id === tier)?.name
        : null;
    },
    getDisplayValuePlan (item) {
      return item
        ? `${item.name} ${item.duration} ${item.period.name} - ${item.price} ${item.currency.code}`
        : null;
    },
    getDisplayValuePlanById (id) {
      const plan = this.getEnrichedSubscriptionsPlans?.find(p => p.id === id);
      return this.getDisplayValuePlan(plan);
    },
    disabledDate (current) {
      return current && current < moment().startOf('day');
    },
  },
  components: {
    Spinner,
    PlaneCheckbox,
    InfoPopup,
    QuestionPopup,
    SvgIcon,
  },
}
</script>

<template>
  <div class="dashboard">
    <vue-scroll :ops="ops">
      <div id="generateContainer">
        <div id="title" class="title">Promo code generator</div>
        <div id="specificationContainer" class="spec">
          <div class="discount-type-switchers">
            <button
              class="discount-type-button"
              :class="{ active: discountType === 'trial' }"
              @click.prevent="setDiscountType('trial')"
            >
              Trial
            </button>
            <button
              class="discount-type-button"
              :class="{ active: discountType === 'percentage' }"
              @click.prevent="setDiscountType('percentage')"
            >
              Percentage
            </button>
            <button
              class="discount-type-button"
              :class="{ active: discountType === 'amount' }"
              @click.prevent="setDiscountType('amount')"
            >
              Amount
            </button>
          </div>

          <div v-if="discountType === 'percentage'" id="percentInput" class="custom-input">
            <div style="margin-bottom: 5px">Percent</div>
            <input class="field"
                   v-model="percent"
                   v-mask="'##'"
                   type="text"/>
          </div>

          <div v-if="discountType === 'amount'" id="amountInput" class="custom-input">
            <div style="margin-bottom: 5px">Amount, $</div>
            <input class="field"
                   v-model="amount"
                   v-mask="'####'"
                   type="text"/>
          </div>

          <template v-if="discountType === 'trial'">
            <div style="margin-top: 25px">Trial period</div>
            <select class="selectPeriod"
                    :class="{ default: selectedTrialPeriod === '' }"
                    style="margin-top: 5px"
                    v-model="selectedTrialPeriod">
              <option class="defaultOption" value=''/>
              <option
                class="option"
                v-for="period in this.getPeriods()"
                :key="period.id"
                :value="period.id">
                {{ period.name }}
              </option>
            </select>
            <div id="durationInput" style="margin-top: 25px">
              <div style="margin-bottom: 5px">Trial duration</div>
              <input id="trialDurationInput"
                     class="field"
                     v-model="trialDuration"
                     v-mask="'###'"
                     type="text"/>
            </div>
          </template>

          <div style="margin-top: 25px">Tier</div>
          <select class="selectPeriod"
                  :class="{ default: selectedTier === '' }"
                  style="margin-top: 5px"
                  v-model="selectedTier">
            <option class="defaultOption" value=''/>
            <option
              class="option"
              v-for="tier in tiers"
              :key="tier.id"
              :value="tier.id">
              {{ tier.name }}
            </option>
          </select>
          <div style="margin-top: 25px">Plan</div>
          <select class="selectPeriod"
                  :class="{ default: selectedPlan === '' }"
                  style="margin-top: 5px"
                  v-model="selectedPlan">
            <option class="defaultOption" value=''/>
            <option
              class="option"
              v-for="plan in getActiveEnrichedSubscriptionsPlans"
              :key="plan.id"
              :value="plan.id">
              {{ getDisplayValuePlan(plan) }}
            </option>
          </select>
        </div>
        <div id="isOneTimeCheckBox"
             class="oneTimeCheckbox"
             style="margin-left: 20px; margin-top: 25px;">
          <PlaneCheckbox
            :isChecked.sync="isOneTime"
            label="SINGLE USE"
          />
        </div>
        <div style="margin-top: 25px">Attributed to</div>
        <div id="attributedToContainer" class="promo" style="margin-top: 5px">
          <input id="attributedToContainer"
                 class="field"
                 v-model="attributedTo"
                 type="text"/>
        </div>
        <div style="margin-top: 25px">Expires</div>
        <div class="promo" style="margin-top: 5px">
          <a-date-picker
            class="field field--date-picker"
            placeholder="Select expiration date"
            v-model="expires"
            :showToday="false"
            :allowClear="true"
            :format="dateFormat"
            :disabledDate="disabledDate"
            :getCalendarContainer="getPickerContainer"
          />
        </div>
        <div style="margin-top: 25px">Code</div>
        <div id="promoContainer" class="promo" style="margin-top: 5px">
          <input id="promoInput"
                 class="field promo"
                 v-model="promoCode"
                 type="text"/>
          <button id="copyPromoButton"
                  class="buttonCopy input"
                  @click.prevent="copy(promoCode)">
              <span>
                  <SvgIcon name="copy"/>
              </span>
          </button>
        </div>
        <div id="buttonsContainer" class="promo" style="margin-top: 25px">
          <button id="generateButton"
                  class="promoButton"
                  style="margin-right: 10px"
                  @click.prevent="generatePromoCode">
            <span class="text">Generate</span>
          </button>
          <button id="saveButton"
                  class="promoButton"
                  style="margin: 0 10px"
                  @click.prevent="savePromoCode">
            <span class="text">Save code</span>
          </button>
        </div>
      </div>
      <div
        class="title"
        style="margin-top: 25px; margin-bottom: 25px">Active promo code</div>
      <table id="promoTable"
             class="table">
        <thead>
        <tr class="table-header">
          <th class="table-cell">Code</th>
          <th class="table-cell">Attributed to</th>
          <th class="table-cell">Tier</th>
          <th class="table-cell">Plan</th>
          <th class="table-cell">Amount</th>
          <th class="table-cell">Percent</th>
          <th class="table-cell">Trial</th>
          <th class="table-cell">Created date</th>
          <th class="table-cell">Expires</th>
          <th class="table-cell">Single use</th>
          <th class="table-cell"></th>
        </tr>
        </thead>
        <tbody class="table-body">
        <tr v-for="(item, index) in promoCodes" :key="index" class="table-row">
          <td class="table-cell">
            <span class="mobile-label">CODE:</span>
            <button id="copyPromoButton" class="buttonCopy" @click.prevent="copy(item.code)">
                <span>
                  <SvgIcon name="copy"/>
                </span>
            </button>
            <span style="text-transform: none; font-weight: bolder" @click.prevent="copy(item.code)">{{ item.code }}</span>
          </td>
          <td class="table-cell">
            <span class="mobile-label">Attributed to:</span>
            <span>{{ item.name }}</span>
          </td>
          <td class="table-cell">
            <span class="mobile-label">Tier:</span>
            <span>{{ getTierName(item.subscriptionPlanTier) }}</span>
          </td>
          <td class="table-cell">
            <span class="mobile-label">Plan:</span>
            <span>{{ getDisplayValuePlanById(item.subscriptionPlanId) }}</span>
          </td>
          <td class="table-cell">
            <span class="mobile-label">Amount:</span>
            <span>{{ item.amount ? `$${item.amount}` : "" }}</span>
          </td>
          <td class="table-cell">
            <span class="mobile-label">Percent:</span>
            <span>{{ item.percent ? `${item.percent}%` : "" }}</span>
          </td>
          <td class="table-cell">
            <span class="mobile-label">Trial:</span>
            <span>{{ getDisplayTrial(item) }}</span>
          </td>
          <td class="table-cell">
            <span class="mobile-label">Created date:</span>
            <span>{{ getDisplayDate(item.createdOn) }}</span>
          </td>
          <td class="table-cell">
            <span class="mobile-label">Expires:</span>
            <span>{{ getDisplayDate(item.expires, false) }}</span>
          </td>
          <td class="table-cell">
            <span class="mobile-label">Single use:</span>
            <PlaneCheckbox :isChecked="item.isOneTime" :isDisabled="true"/>
          </td>
          <td class="table-cell">
            <span class="mobile-label">Actions:</span>
            <div
              class="promo-remove-action"
              @click.prevent="showRemovePromoCodeModal(item.id)"
            >
              <SvgIcon name="close"/>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
      <InfoPopup/>
      <QuestionPopup
        :name="'questionPopupPromoCodeRemove'"
        :title="'Promo code will be removed'"
        :text="'Are you sure?'"
        @remove="removePromoCode"
      />
    </vue-scroll>
    <div v-if="loading" class="spinner-overlay">
      <Spinner/>
    </div>
  </div>
</template>
